import { useRef, useState, useEffect } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { Title } from "../../../../themes/Default/components/Title";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Document } from "../../../../themes/Default/components/Form/components/Document";
import { Documents as DocumentsWrapper } from "../../../../themes/Default/components/Documents";
import { Item } from "../../../../themes/Default/components/Documents/components/Item";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { employeesManagement } from "../../../../hooks/api/EmployeesManagement";
import Toaster from "../../../../themes/Default/components/Toaster/Toaster";

const Documents = () => {
    const formRef = useRef(null);
    const { employeeId } = useParams();
    const { t } = useTranslation("common");
    const [documents, setDocuments] = useState([]);
    const [employee, setEmployee] = useState();
    const navigate = useNavigate();
    const { useAddDocumentMutation, useListDocumentsMutation, useDeleteDocumentMutation, useViewEmployeeMutation, useViewDocumentMutation } = employeesManagement;
    const [addDocument] = useAddDocumentMutation();
    const [viewEmployee] = useViewEmployeeMutation();
    const [listDocuments] = useListDocumentsMutation();
    const [deleteDocument] = useDeleteDocumentMutation();
    const [downloadDocument] = useViewDocumentMutation();

    const gotoDetailsTab = (employeeId) => {
        navigate("/employees/edit/" + employeeId);
    };

    const gotoNotesTab = (employeeId) => {
        navigate("/employees/" + employeeId + "/notes");
    };

    const gotoEmployeesList = () => {
        navigate("/employees");
    };

    const tabs = [
        { title: t("details"), link: "/settings/reports", onClick: () => gotoDetailsTab(employeeId) },
        { title: t("documents"), link: "/settings/reports", isActive: true },
        { title: t("notes"), link: "/settings/reports", onClick: () => gotoNotesTab(employeeId) },
    ];

    const onDocumentDelete = (event, documentId) => {
        event.preventDefault();
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        deleteDocument({ employeeId, documentId }).then((result) => {
                            toast.info(result?.data?.info?.responseMessage);
                            viewDocuments();
                        });
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onDocumentDownload = async (event, documentId) => {
        event.preventDefault();
        const blob = await downloadDocument({ employeeId, documentId }).unwrap();

        const docToDownload = documents.find((doc) => doc.id === documentId);
        let fileName = docToDownload?.fileName ?? "dokument";
        if (docToDownload?.extension) {
            fileName += '.' + docToDownload.extension;
        }

        const urlBlob = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = urlBlob;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(urlBlob);
    };

    const viewDocuments = () => {
        listDocuments(employeeId).then((result) => {
            const documents = result?.data?.records;
            setDocuments(documents);
        });
    };

    useEffect(() => {
        viewDocuments();
    }, []);

    useEffect(() => {
        viewEmployee(employeeId).then((result) => {
            setEmployee(result?.data);
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/employees" />
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>
                    {employee?.firstName} {employee?.lastName}
                </Title>
                <Breadcrumps>
                    <Link title={t("employees.title")} icon="fa-user-tie" />
                    <Link title={t("documents")} />
                    <Link title="List" isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        document: null,
                    }}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Document
                                        name="document"
                                        onChange={(event) => {
                                            const file = event.currentTarget.files[0];
                                            setFieldValue("document", file);
                                            addDocument({ file, employeeId }).then((result) => {
                                                if (result?.error) {
                                                    toast.error(result.error.data.error.responseMessage);
                                                } else {
                                                    toast.info(result?.data?.info?.responseMessage);
                                                    listDocuments(employeeId).then((result) => {
                                                        const documents = result?.data?.records;
                                                        setDocuments(documents);
                                                    });
                                                }
                                            });
                                        }}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <DocumentsWrapper>
                                        {documents?.length > 0 &&
                                            documents?.map((document, index) => {
                                                return (
                                                    <Item
                                                        id={document.id}
                                                        key={index}
                                                        name={document.fileName}
                                                        type={document.mimeType}
                                                        onDelete={(event) => onDocumentDelete(event, document.id)}
                                                        onDownload={(event) => onDocumentDownload(event, document.id)}
                                                    />
                                                );
                                            })}
                                    </DocumentsWrapper>
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default Documents;
